import * as Sentry from '@sentry/react'
import { createRouter } from '@tanstack/react-router'
import Default404 from './components/Default404'
import DefaultError from './components/DefaultError'
import DefaultPending from './components/DefaultPending'
import { setRouterGetter } from './lib/auth-response-handler'
import { enableBrowserTranslationSafeNavigation } from './lib/browser-translation'
import { beforeSendAdminEvent } from './lib/sentry'
import { routeTree } from './routeTree.gen'

export const router = createRouter({
  routeTree,
  scrollRestoration: true,
  context: {
    authState: undefined,
    // @ts-expect-error RouterProvider supplies the live QueryClient before the first route load; retain the native bootstrap placeholder.
    queryClient: undefined,
    // @ts-expect-error RouterProvider supplies the live AppAbility before the first route load; retain the native bootstrap placeholder.
    ability: undefined,
  },
  defaultStructuralSharing: true,
  defaultPreload: 'intent', // enable pre-loading on all <Link> components
  defaultPreloadStaleTime: 0, // Allow Tanstack Query to handle stale time and de-duping requests
  defaultPendingComponent: () => <DefaultPending />,
  defaultNotFoundComponent: () => <Default404 />,
  defaultErrorComponent: ({ error }) => <DefaultError error={error} />,
})

enableBrowserTranslationSafeNavigation(router)

// Set up the router getter for detectLoggedOutResponse to avoid circular dependencies
setRouterGetter(() => router)

if (import.meta.env.VITE_ENV !== 'local') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV,
    release:
      import.meta.env.VITE_ADMIN_BUILD_ID ||
      import.meta.env.VITE_AWS_COMMIT_ID ||
      'unknown-release',

    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
    sendDefaultPii: true,

    integrations: [
      // Use the browser tracking integration that matches your router (React Router, Tanstack Router, etc)
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration(),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for tracing.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
    tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? ''),

    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    // tracePropagationTargets: [/^\//, /^https:\/\/yourserver\.io\/api/],
    tracePropagationTargets:
      import.meta.env.VITE_API_URL === undefined ? [] : [import.meta.env.VITE_API_URL],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
    replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SAMPLE_RATE ?? ''),
    replaysOnErrorSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? ''
    ),
    beforeSend: beforeSendAdminEvent,
  })
}

// detectLoggedOutResponse is now exported from lib/auth-response-handler.js
// to avoid circular dependencies during HMR
export { detectLoggedOutResponse } from './lib/auth-response-handler'
